import { createRef, FC, RefObject } from 'react';
import { Stack, TextField } from '@mui/material';

type Props = {
  valueArray: string[];
  onChange: (value: string[]) => void;
};

const SplitInput: FC<Props> = ({ onChange, valueArray }) => {
  const inputRefs = Array.from(
    valueArray.map(() => createRef<HTMLInputElement>()),
  );

  const focusOnElement = (ref: RefObject<HTMLInputElement>) => {
    ref.current?.focus();
    ref.current?.select();
  };

  const setNewValue = (newValue: string, index: number) => {
    const newValueArray = [...valueArray];
    newValueArray[index] = newValue;
    onChange(newValueArray);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const newValue = e.target.value.toUpperCase();
    setNewValue(newValue, index);

    // Focus on the next input
    if (newValue && inputRefs[index + 1]) {
      focusOnElement(inputRefs[index + 1]);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').toUpperCase();
    const pasteArray = paste.split('');
    const newValueArray = [...valueArray];

    // Iterate over the pasted characters
    pasteArray.forEach((char, i) => {
      // If there is a corresponding input reference and it's within the length limit
      if (inputRefs[i] && i < newValueArray.length) {
        // Replace the character in the new value array
        newValueArray[i] = char;
      }
    });

    // Update the value with the new array joined back into a string
    onChange(newValueArray);

    // Focus the last input that was filled
    focusOnElement(inputRefs[newValueArray.length - 1]);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace' && !valueArray[index] && inputRefs[index - 1]) {
      e.preventDefault();
      setNewValue('', index - 1);
      focusOnElement(inputRefs[index - 1]);
    }
  };

  return (
    <Stack gap={1} direction="row">
      {inputRefs.map((ref, index) => (
        <TextField
          key={index}
          variant="outlined"
          slotProps={{
            htmlInput: {
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontWeight: 'bold',
              },
            },
          }}
          value={valueArray[index] || ''}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          inputRef={ref}
          autoFocus={index === 0}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </Stack>
  );
};

export default SplitInput;
